import React, {useState, useMemo, useEffect, useRef} from 'react';
import {Container, Image, Panel, Row} from '@wonderlandengine/react-ui/components';
import {Align, Justify, PositionType, YogaNodeProps} from '@wonderlandengine/react-ui';
import {colorSwatch} from '../utils/colorSwatch.js';
import {parseColor} from '../utils/utils.js';
import {useRotBMaterialContext} from '../utils/rotb-material-context.js';
import {FlatOpaqueTextured} from '../../types/materials.js';
import {Slider, SliderItem, SliderRef} from './SliderComponent.js';
import {UiTexturesManager} from './ui-textures-manager.js';
import {RotatableImage} from './rotatable-image.js';
import {useMemoMaterial} from '../utils/MaterialPool.js';

const _mapSliderItems = (children: React.ReactNode): SliderItem[] => {
    return React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) {
            return null;
        }
        const item = child.props as SliderItem;
        if (!item || item.id === undefined) {
            console.warn('Carousel child missing props or unique id/key.', child);
            return null;
        }
        return item;
    }).filter((item): item is SliderItem => item !== null);
};

export const CarouselItem = (props: SliderItem) => {
    // doesn't render anything, just a placeholder for the carousel
    return <></>;
};

export const Carousel = (
    props: React.PropsWithChildren<YogaNodeProps> & {
        loop: boolean;
        onSelectionChanged?: (item: SliderItem) => void;
    }
) => {
    const rotbMC = useRotBMaterialContext();

    const ground = useMemoMaterial(
        'CarouselGround',
        () => {
            const m = rotbMC?.gradientMaterial.clone() as FlatOpaqueTextured;
            m.setColor(parseColor(colorSwatch.SliderGround));
            return m;
        },
        [props]
    );

    const [sliderItems, setSliderItems] = useState<SliderItem[]>([]);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [canScrollPrevious, setCanScrollPrevious] = useState(false);

    const stableChildrenKeys = useMemo(() => {
        try {
            return JSON.stringify(
                React.Children.map(props.children, (child) => {
                    if (React.isValidElement(child)) {
                        return child.key ?? (child.props as SliderItem).id ?? null;
                    }
                    return null;
                }).filter((key) => key !== null) // Filter out null keys/ids
            );
        } catch (e) {
            console.error('Error creating stable children keys:', e);
            // Fallback: Use a changing value to force updates on error, less ideal
            return Math.random();
        }
    }, [props.children]);

    const memoizedSliderItems = useMemo(() => {
        return _mapSliderItems(props.children);
        // Depend on the stable representation, NOT props.children directly
    }, [stableChildrenKeys]);

    useEffect(() => {
        setSliderItems(memoizedSliderItems);
    }, [memoizedSliderItems]); // Depend only on the memoized value

    // reference to call into the Slider component
    const sliderRef = useRef<SliderRef>(null);

    // To go to next item
    const handleNextClick = () => {
        sliderRef.current?.next();
    };

    // To go to previous item
    const handlePrevClick = () => {
        sliderRef.current?.previous();
    };
    // Update scroll status
    const updateScrollState = () => {
        if (sliderRef.current) {
            setCanScrollNext(sliderRef.current.canScrollNext);
            setCanScrollPrevious(sliderRef.current.canScrollPrevious);
        }
    };
    // Initialize and update scroll status when slider is ready
    useEffect(() => {
        if (sliderRef.current) {
            updateScrollState();
        }
    }, [sliderItems]);

    // Update scroll status when selection changes
    const handleSelectionChange = (i: number) => {
        if (props.onSelectionChanged) {
            props.onSelectionChanged(sliderItems[i]);
        }
        updateScrollState();
    };

    return (
        <Panel
            width={500}
            height={props.height ?? 200}
            rounding={0}
            backgroundColor={colorSwatch.SliderBackground}
        >
            <Panel
                material={ground}
                width="100%"
                top={((props.height as number) ?? 200) - 100}
                height={100}
                rounding={0}
                border={0}
            ></Panel>
            <Slider
                ref={sliderRef}
                top={-45}
                width={500}
                height={200}
                justifyContent={Justify.Center}
                z={0.01}
                items={sliderItems}
                onSelectionChange={handleSelectionChange}
            ></Slider>
            <Row
                position={PositionType.Absolute}
                width={480}
                height={props.height ?? 200}
                justifyContent={Justify.SpaceBetween}
                alignItems={Align.Center}
                alignSelf={Align.Center}
            >
                <Container width={25} height={25}>
                    {canScrollPrevious && (
                        <RotatableImage
                            rounding={0}
                            width={25}
                            height={25}
                            src={UiTexturesManager.instance.arrow}
                            onClick={handlePrevClick}
                            rotation={180}
                        />
                    )}
                </Container>

                <Container width={25} height={25}>
                    {canScrollNext && (
                        <Image
                            rounding={0}
                            width={25}
                            height={25}
                            src={UiTexturesManager.instance.arrow}
                            onClick={handleNextClick}
                        />
                    )}
                </Container>
            </Row>
        </Panel>
    );
};
